import {
  LinkSimple,
  Seal,
  SealCheck,
  Warning,
  XCircle,
} from '@phosphor-icons/react';
import { AnalyzerPodReport } from '@Models/types';
import DOMPurify from 'dompurify';
import { useAnalyzerReportsContext } from '@Context/AnalyzerReportsContext';
import { computeTicketCost } from '@Utils/tokenCostUtils';

const formatBoldText = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold text-gray-900">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

const formatExplanation = (explanation: string) => {
  const hasNumberedPoints = /^\d+\.|(?<=\n)\d+\./.test(explanation);

  if (hasNumberedPoints) {
    const points = explanation
      .split(/(?=^\d+\.|(?<=\n)\d+\.)/m)
      .filter((point) => point.trim().length > 0)
      .map((point) => point.trim());

    return (
      <div className="space-y-2">
        {points.map((point, index) => {
          const numberMatch = point.match(/^(\d+)\./);
          if (numberMatch) {
            return (
              <div
                key={index}
                className="flex items-start gap-2 text-sm text-gray-700"
              >
                <span className="flex-shrink-0 font-medium text-gray-900">
                  {numberMatch[0]}
                </span>
                <span className="flex-1">
                  {formatBoldText(point.replace(/^\d+\.\s*/, ''))}
                </span>
              </div>
            );
          }
          return (
            <div key={index} className="text-sm text-gray-700">
              {formatBoldText(point)}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="text-sm leading-relaxed text-gray-700">
      {formatBoldText(explanation)}
    </div>
  );
};

type TicketCardProps = {
  ticket: AnalyzerPodReport['tickets'][0];
};

const getConfidenceIcon = (score: number) => {
  if (score >= 70) return <SealCheck className="text-green-600" size={20} />;
  if (score >= 30) return <Warning className="text-yellow-500" size={20} />;
  return <XCircle className="text-red-500" size={20} />;
};

export function TicketCard({ ticket }: TicketCardProps) {
  const {
    selectedTickets,
    setSelectedTickets,
    reports,
    selectedTicket,
    setSelectedTicket,
  } = useAnalyzerReportsContext();

  const isSelected = !!selectedTickets[ticket.ticket.reference_num];
  const taggedTicket = ticket.ticket.tagged ? ticket.ticket.tagged : false;

  const handleToggleSelect = () => {
    setSelectedTickets((prev) => {
      const copy = { ...prev };
      if (copy[ticket.ticket.reference_num]) {
        delete copy[ticket.ticket.reference_num];
      } else {
        copy[ticket.ticket.reference_num] = {
          ticket: ticket,
          week:
            Object.entries(reports).find(([, r]) =>
              r.tickets.some(
                (t) => t.ticket.reference_num === ticket.ticket.reference_num
              )
            )?.[1]?.week ?? 'unknown',
        };
      }
      return copy;
    });

    if (
      selectedTicket &&
      selectedTicket.ticket.reference_num === ticket.ticket.reference_num
    ) {
      setSelectedTicket(null);
    }
  };

  const hasClassification = !!ticket.ticket.classification;
  const { blendedCost, inputCost, outputCost } = computeTicketCost(ticket);

  return (
    <div className="custom-scroll h-full overflow-auto p-4">
      <div className="mb-4 flex items-center justify-between">
        <a
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-row items-center gap-2 text-lg font-semibold text-primary-700 hover:cursor-pointer hover:text-primary-400"
          href={`https://trajector1.aha.io/develop/features/${ticket.ticket.reference_num}`}
        >
          <LinkSimple size={20} weight="bold" />
          <label className="hover:cursor-pointer">
            {ticket.ticket.reference_num}: {ticket.ticket.name}
          </label>
        </a>
        <span className="text-sm text-gray-500">
          {ticket.ticket.new_ticket && (
            <span className="flex flex-col items-center text-xs italic">
              <Seal size={25} weight="fill" className="text-brandSuccess-500" />
              <label className="text-brandSuccess-400">New</label>
            </span>
          )}
        </span>
      </div>
      <div
        className="adf-body mb-4 text-xs"
        dangerouslySetInnerHTML={{
          // Aha-sourced HTML; DOMPurify defaults are sufficient here.
          __html: DOMPurify.sanitize(ticket.ticket.description.body || ''),
        }}
      />
      {hasClassification ? (
        <>
          <div className="mb-1 flex items-center gap-2 text-sm">
            <span className="font-medium">Confidence:</span>
            {getConfidenceIcon(ticket.ticket.classification.confidence_score)}
            <span>{ticket.ticket.classification.confidence_score}%</span>
          </div>
          <div className="mb-2 text-sm">
            <span className="font-medium">Production Bug:</span>{' '}
            {ticket.ticket.classification.production_bug}
          </div>
          <div className="mb-4 mt-3">
            {formatExplanation(ticket.ticket.classification.explanation)}
          </div>
        </>
      ) : (
        <div className="my-2 text-sm italic text-red-400">
          No classification available.
        </div>
      )}

      <div className="mb-2 mt-4">
        <div className="mb-1 flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-900">Cost:</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span>Input: ${inputCost.toFixed(4)}</span>
          <span>Output: ${outputCost.toFixed(4)}</span>
        </div>
        <div className="mt-1 text-xs text-gray-600">
          <span className="font-medium">Total:</span>{' '}
          <span
            title={`Input: $${inputCost.toFixed(4)}, Output: $${outputCost.toFixed(4)}`}
          >
            ${blendedCost.toFixed(4)}
          </span>
          <span className="ml-2 text-gray-400">(blended)</span>
        </div>
        <div className="mt-1 text-xs text-gray-600">
          <span className="font-medium">Model:</span>{' '}
          <span title={`Model: ${ticket.ticket.model}`}>
            {ticket.ticket.model}
          </span>
        </div>
      </div>
      <div className="text-xs text-gray-500">
        <span className="font-medium">Created:</span>{' '}
        {new Date(ticket.ticket.created_at).toLocaleString()}
        <span className="mx-2">|</span>
        <span className="font-medium">Updated:</span>{' '}
        {new Date(ticket.ticket.updated_at).toLocaleString()}
      </div>
      <div className="mt-5 flex justify-end">
        {!taggedTicket && (
          <button
            className="rounded-md bg-primary-700 px-4 py-3 text-white"
            onClick={handleToggleSelect}
          >
            {isSelected ? 'Untag Ticket' : 'Tag Ticket'}
          </button>
        )}
      </div>
    </div>
  );
}
